import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  Inject,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { EncrDecrService } from '@mysrc/app/Providers/EncrDecrService';
import { BaseHttpReqRespService } from '@mysrc/app/modules/fwcore/services';
import { IsgGridComponent } from '@mysrc/app/shared/components/isg-grid';
import { IsgGridConfig } from '@mysrc/app/shared/models/grid-config.model';
import { NgbModal, NgbModalRef, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import {
  Workbook,
  WorkbookSheet,
  WorkbookSheetRow,
  WorkbookSheetRowCell
} from '@progress/kendo-angular-excel-export';
import { saveAs } from '@progress/kendo-file-saver';

import { CommonService } from '../../../question/services/commonservice';
import { CompanyService } from '../../services/company.service';
import { Company, MasterCompanyDetail } from '../../models/company.model';

@Component({
  selector: 'company',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgbModule, AgGridAngular, IsgGridComponent],
  templateUrl: './page-company.component.html',
  styleUrls: ['./page-company.component.css'],
  providers: [CommonService, CompanyService],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class PageCompanyComponent extends BaseHttpReqRespService implements OnInit {

  // ── ViewChild modal refs (same NgbModal pattern as user) ───
  @ViewChild('companyModal') companyModal!: TemplateRef<any>;
  @ViewChild('deleteCompanyModal') deleteCompanyModal!: TemplateRef<any>;

  modalRef: NgbModalRef | undefined;

  // ── Forms ──────────────────────────────────────────────────
  companyForm!: FormGroup;

  // ── Grid data ──────────────────────────────────────────────
  companyList: Company[] = [];

  // ── State ──────────────────────────────────────────────────
  currentCompanyId: number = -1;
  currentDeleteId: number = 0;
  tmpWorkspaceId: string = '';
  successOrFailureMessage: string = '';
  imgURL: string | null = null;
  validImage: boolean = false;

  private base64Image: string = '';
  private gridApi: any;

  // ── ISG Grid config (same pattern as user) ─────────────────
  gridConfig: Partial<IsgGridConfig> = {
    height: '500px',
    pagination: true,
    pageSize: 10,
    autoSizeColumns: true
  };

  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true
  };

  // ── Column definitions ─────────────────────────────────────
  columnDefs: ColDef[] = [
    { field: 'companyId', hide: true },
    {
      headerName: 'Service Provider',
      field: 'companyName',
      resizable: true,
      cellRenderer: (params: any) => this.editLinkRenderer(params)
    },
    { headerName: 'Created By', field: 'createdByName', resizable: true },
    { headerName: 'Modified By', field: 'modifiedByName', resizable: true },
    {
      headerName: 'Created Date',
      field: 'createdDate',
      resizable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString() : ''
    },
    {
      headerName: 'Modified Date',
      field: 'modifiedDate',
      resizable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString() : ''
    },
    {
      headerName: 'Delete',
      width: 100,
      cellRenderer: (params: any) => this.deleteRenderer(params)
    }
  ];

  constructor(
    @Inject(OKTA_AUTH) private oauth: OktaAuth,
    protected http: HttpClient,
    private EncrDecr: EncrDecrService,
    private formBuilder: FormBuilder,
    private renderer: Renderer2,
    public modalService: NgbModal,
    private companyService: CompanyService,
    @Inject('BASE_URL') baseUrl: string
  ) {
    super(http, oauth);
  }

  ngOnInit(): void {
    this.tmpWorkspaceId = this.EncrDecr.get(localStorage.getItem('workspaceId') ?? '');
    this.initForm();
    this.loadCompanyList();
  }

  // ── Form initialisation ────────────────────────────────────
  private initForm(): void {
    this.companyForm = this.formBuilder.group({
      companyId: new FormControl(0),
      companyName: new FormControl('', [
        Validators.required,
        this.noWhitespaceValidator,
        this.companyExistsValidator()
      ]),
      companyDescription: new FormControl(''),
      imageFile: new FormControl(''),
      notifyMe: new FormControl(false)
    });
  }

  // ── Load company list ──────────────────────────────────────
  loadCompanyList(): void {
    this.companyService.getCompanyList(this.tmpWorkspaceId).subscribe({
      next: (result: Company[]) => this.companyList = result,
      error: (err: unknown) => console.error(err)
    });
  }

  // ── Grid ready ─────────────────────────────────────────────
  onGridReady(params: any): void {
    this.gridApi = params.api;
    params.api.sizeColumnsToFit();
  }

  // ── Open modals (NgbModal pattern — same as user) ──────────
  openCreateCompanyModal(): void {
    this.currentCompanyId = -1;
    this.resetFormState();
    this.modalRef = this.modalService.open(this.companyModal, {
      backdrop: 'static',
      keyboard: false
    });
  }

  openDeleteCompanyModal(): void {
    this.modalRef = this.modalService.open(this.deleteCompanyModal, {
      backdrop: 'static',
      keyboard: false
    });
  }

  // ── Close / reset ──────────────────────────────────────────
  onClose(): void {
    this.resetFormState();
  }

  private resetFormState(): void {
    this.imgURL = null;
    this.base64Image = '';
    this.validImage = false;
    this.initForm();
  }

  // ── Cell renderers (Renderer2 — same as user) ──────────────
  editLinkRenderer(params: any): HTMLElement {
    const element = this.renderer.createElement('a');
    const text = this.renderer.createText(params.value);
    this.renderer.appendChild(element, text);
    this.renderer.setAttribute(element, 'href', 'javascript:void(0)');
    this.renderer.listen(element, 'click', () => {
      const userId = this.EncrDecr.get(localStorage.getItem('userId') ?? '');
      this.currentCompanyId = params.data.companyId;
      this.companyService.editCompany(params.data.companyId, userId).subscribe({
        next: (response: Company) => {
          this.imgURL = response.imageFile ?? null;
          this.companyForm.patchValue({
            companyId: response.companyId,
            companyName: response.companyName,
            companyDescription: response.companyDescription,
            imageFile: response.imageFile
          });
          this.modalRef = this.modalService.open(this.companyModal, {
            backdrop: 'static',
            keyboard: false
          });
        },
        error: (err: unknown) => console.error(err)
      });
    });
    return element;
  }

  deleteRenderer(params: any): HTMLElement {
    const element = this.renderer.createElement('a');
    const icon = this.renderer.createElement('i');
    icon.setAttribute('class', 'fa fa-trash anchor-cursor');
    this.renderer.appendChild(element, icon);
    this.renderer.listen(element, 'click', () => {
      this.currentDeleteId = params.data.companyId;
      this.openDeleteCompanyModal();
    });
    return element;
  }

  // ── Save company ───────────────────────────────────────────
  saveCompany(): void {
    if (this.companyForm.invalid) return;
    this.companyForm.patchValue({ imageFile: this.base64Image });
    this.companyService.saveCompany(this.companyForm.value).subscribe({
      next: (result: Company[]) => {
        this.companyList = result;
        this.successOrFailureMessage = 'Service Provider saved successfully.';
        this.messageTimeout(2500);
        this.onClose();
      },
      error: (err: unknown) => console.error(err)
    });
  }

  // ── Delete company ─────────────────────────────────────────
  deleteCompany(): void {
    this.companyService.deleteCompany(this.currentDeleteId).subscribe({
      next: () => {
        this.loadCompanyList();
        this.messageTimeout(2500);
      },
      error: (err: unknown) => console.error(err)
    });
  }

  // ── Export master company list ─────────────────────────────
  getMasterCompanyList(): void {
    this.companyService.getMasterCompanyList(this.tmpWorkspaceId).subscribe({
      next: (result: MasterCompanyDetail[]) => this.exportToExcel(result),
      error: (err: unknown) => console.error(err)
    });
  }

  // ── Excel export (Kendo — same pattern as user) ────────────
  exportToExcel(params: any[]): void {
    if (!params?.length) return;

    const columns = Object.keys(params[0]);

    const headerCells: WorkbookSheetRowCell[] = columns.map((col: string) => ({
      value: col,
      bold: true,
      background: '#FFFF00',
      borderBottom: { size: 1, color: '#000000' },
      borderTop: { size: 1, color: '#000000' },
      borderLeft: { size: 1, color: '#000000' },
      borderRight: { size: 1, color: '#000000' }
    }));

    const dataRows: WorkbookSheetRow[] = params.map((item: any) => ({
      cells: columns.map((col: string) => ({ value: item[col] ?? '' }))
    }));

    const sheet: WorkbookSheet = {
      name: 'CompanyList',
      rows: [{ cells: headerCells }, ...dataRows],
      columns: columns.map((col: string) => ({
        width: Math.max(col.length, 15) * 7
      }))
    };

    const workbook = new Workbook({ sheets: [sheet] });
    workbook.toDataURL().then((dataURL: string) => {
      saveAs(dataURL, 'MasterCompanyList.xlsx');
    });
  }

  // ── Image handling ─────────────────────────────────────────
  preview(files: FileList | null): void {
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!file.type.match(/image\/*/)) {
      this.validImage = true;
      return;
    }

    this.validImage = false;
    const reader = new FileReader();

    reader.onload = () => {
      this.imgURL = reader.result as string;

      const img = new Image();
      img.onload = () => {
        if (img.width > 289 || img.height > 55) {
          this.validImage = true;
          this.imgURL = null;
          this.base64Image = '';
          return;
        }
        const binaryReader = new FileReader();
        binaryReader.onload = (e) => {
          this.base64Image = btoa(e.target?.result as string);
        };
        binaryReader.readAsBinaryString(file);
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  // ── Validators ─────────────────────────────────────────────
  noWhitespaceValidator(control: FormControl): { [key: string]: boolean } | null {
    const isWhitespace = (control.value ?? '').trim().length === 0;
    return isWhitespace ? { whitespace: true } : null;
  }

  companyExistsValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const currentId = this.companyForm?.get('companyId')?.value ?? 0;
      const exists = this.companyList.some(
        c => c.companyName?.trim().toLowerCase() === control.value?.trim().toLowerCase()
          && c.companyId !== currentId
      );
      return exists ? { companyExists: true } : null;
    };
  }

  // ── Message helper (same signature as user) ────────────────
  messageTimeout(duration: number): void {
    window.setTimeout(() => {
      this.successOrFailureMessage = '';
    }, duration);
  }
}
